import domReady from '@wordpress/dom-ready';
import { sprintf, _nx } from '@wordpress/i18n';

domReady(() => {
  const findstr = window.findstr;
  const countP = document.querySelector(
    '.block-listing__results-count[data-findstr-group="clinical-documents"]'
  );

  function adjustResultsCount(total) {
    const newText = sprintf(
      /* translators: %s is number of results */
      _nx('%s result', '%s results', total, 'results count', 'vtx'),
      total
    );
    countP.textContent = newText;
  }

  if (findstr && countP) {
    findstr.hooks.addAction(
      'searchResults',
      'icm-clinical-documents',
      (results, group) => {
        if ('clinical-documents' === group) {
          setTimeout(() => adjustResultsCount(results.totalHits), 100);
        }
      }
    );
  }

  initCategoryTopicsFilter();
});

/**
 * Category → Topics dependent filtering via facetDistribution.
 *
 * After each search, the facetDistribution from Meilisearch tells us
 * which topic values exist in the current result set. We use this to
 * filter the hierarchySelect options so only relevant topics are shown.
 *
 * e.g. selecting category "Pharmacie" narrows results, and the
 * facetDistribution only contains topics that have documents in "Pharmacie".
 */
function initCategoryTopicsFilter() {
  const findstr = window.findstr;
  if (!findstr?.hooks) {
    return;
  }

  const topicField = document.querySelector(
    '.findstr-field-hierarchySelect[data-source="tax/clinical-document-topic"]'
  );
  if (!topicField) {
    return;
  }

  const topicInputs = topicField.querySelectorAll(
    '.findstrFieldContainer input[data-findstr]'
  );
  if (!topicInputs.length) {
    return;
  }

  // Store original choices per level from data-hierarchy attributes
  const allChoicesByLevel = [];
  topicInputs.forEach((input, level) => {
    let hierarchy = [];
    try {
      hierarchy = input.dataset.hierarchy
        ? JSON.parse(input.dataset.hierarchy)
        : [];
    } catch (e) {
      // Malformed JSON from server-side rendering, skip this level
      return;
    }
    allChoicesByLevel[level] = Object.values(hierarchy)
      .filter((item) => item.level === level)
      .map((choice) => ({
        id: choice.id,
        value: choice.name,
        parentId: choice.parent,
        text: choice.name,
      }));
  });

  /**
   * Filter topic options using facetDistribution from search results.
   *
   * @param {Object} facetDistribution The facetDistribution from Meilisearch results.
   */
  function filterTopicsByFacets(facetDistribution) {
    if (!facetDistribution) {
      return;
    }

    topicInputs.forEach((input, level) => {
      const ts = input.tomselect;
      if (!ts) {
        return;
      }

      // facetDistribution key matches the input name (e.g. 'clinical-document-topic-lvl-0')
      const availableNames = facetDistribution[input.name];

      // If this level already has a selection, don't disturb it
      // The hierarchy-select plugin handles cascading from selected values
      if (ts.getValue()) {
        return;
      }

      // If no facet data for this level, skip (level might not be active yet)
      if (!availableNames) {
        return;
      }

      const filtered = allChoicesByLevel[level].filter((choice) =>
        Object.hasOwn(availableNames, choice.value)
      );

      // Compare option sets by identity, not just count
      const filteredKeys = new Set(filtered.map((c) => c.value));
      const currentKeys = new Set(Object.keys(ts.options));
      const setsEqual =
        filteredKeys.size === currentKeys.size &&
        [...filteredKeys].every((k) => currentKeys.has(k));

      if (!setsEqual) {
        ts.clearOptions();
        ts.addOptions(filtered);
        ts.refreshOptions(false);
      }
    });
  }

  // Listen for search results to update topic options based on facetDistribution
  // Findstr hooks fire after field initialization, so TomSelect is available
  findstr.hooks.addAction(
    'searchResults',
    'icm-category-topics-facet-filter',
    (results, group) => {
      if ('clinical-documents' !== group) {
        return;
      }
      filterTopicsByFacets(results?.facetDistribution);
    }
  );

  // On reset, restore all original topic options
  findstr.hooks.addAction(
    'resetFilters',
    'icm-category-topics-facet-filter',
    (group) => {
      if ('clinical-documents' !== group) {
        return;
      }

      topicInputs.forEach((input, level) => {
        const ts = input.tomselect;
        if (!ts) {
          return;
        }
        ts.clearOptions();
        ts.addOptions(allChoicesByLevel[level]);
        ts.refreshOptions(false);
      });
    }
  );
}
